/* eslint-disable */
import React, { useMemo } from 'react'
import { useEffect, useRef, useState, useCallback } from 'react'
import { Canvas, ThreeEvent, useThree, useFrame } from '@react-three/fiber'
import { useGLTF, MeshReflectorMaterial, BakeShadows, PerformanceMonitor } from '@react-three/drei'
import { EffectComposer, Bloom, DepthOfField, ToneMapping } from '@react-three/postprocessing'
import { CameraControls } from '../Camera/CameraControls'
import Background from '../Background/Background'
import { Vector3, Color, Euler } from 'three'
import './Scene.css';
import { Bounds, AppMessage, SetUserTableValueWorldMessage } from '../../../models/Types'
import { useParticipantIds, useAppMessage, useLocalSessionId, useDaily } from '@daily-co/daily-react'
import HUDCameraControls from '../../react/HUD/CameraControls/CameraControls'
import { getAudioConfig, DEFAULT_AUDIO_CONFIG, AudioConfig } from '../../../utils/audio-config'
import HUDSelfView from '../../react/HUD/SelfView/SelfView'
import DailyUserAvatar from '../UserAvatar/DailyUserAvatar'
import TestUserAvatar from '../UserAvatar/TestUserAvatar'
import CircularVirtualTable from '../Table/CircularVirtualTable'
import Floor from '../Floor/Floor'
import { CameraControlsProvider } from '../../react/context/CameraControlsCallbackContext'
import { TableCameraProvider } from '../Table/UseTableCamera'
import { DailyEventObjectParticipant } from '@daily-co/daily-js'
import HUDMenu from '../../react/HUD/Menu/Menu'

interface SceneProps {}

interface UserTableProps {
  tableIndex: number;
  seatIndex: number;
  audioConfig: AudioConfig;
}

const unseatedUserTableProps: UserTableProps = { tableIndex: -1, seatIndex: -1, audioConfig: DEFAULT_AUDIO_CONFIG };

const Scene: React.FC<SceneProps> = ({ }) => {
  
  const [dpr, setDpr] = useState(window.devicePixelRatio);
  const [antialias, setAntialias] = useState(false);
  const [powerPreference, setPowerPreference] = useState<'high-performance' | 'low-power' | 'default'>('high-performance');
  console.log('scene rendered', dpr, antialias, powerPreference);
  const containerRef = useRef<HTMLDivElement>(null);

  const localParticipantId = useLocalSessionId();
  const sendAppMessage = useAppMessage();
  const callObject = useDaily();
  const remoteParticipantIds = useParticipantIds({ filter: 'remote',
                                                   onParticipantJoined: (p: DailyEventObjectParticipant) => {
                                                     sendUserTableValueMessage(p.participant.session_id)
                                                     userTableMsgs.current += 1;
                                                    //  if (userTableMsgs.current === remoteParticipantIds.length) tableRef.current.assignSeat()
                                                   }
                                                 });

  const bounds: Bounds = { min: new Vector3(-50, 0, -50), max: new Vector3(50, 5, 50)};

  const userTableProps = useRef<{ [userId: string]: UserTableProps }>({ [localParticipantId]: unseatedUserTableProps });
  const tableRef = useRef(null!);
  const userTableMsgs = useRef(0);

  const tables = useMemo(() => {
    const result = [{ tableIndex: 0, position: new Vector3(0, 0.01, -4), radius: 7, numSeats: 6 }];
    if (remoteParticipantIds.length >= 8) result.push({ tableIndex: 1, position: new Vector3(-28, 0.01, 30), radius: 9, numSeats: 9 });
    if (remoteParticipantIds.length >= 11) result.push({ tableIndex: 2, position: new Vector3(28, 0.01, 28), radius: 11, numSeats: 12 });
    return result;
  }, [remoteParticipantIds.length]);

  const dailyUserAvatars = useMemo(() => (
    remoteParticipantIds.map((id) => (
      <DailyUserAvatar key={id} userId={id} audioConfig={userTableProps.current[id].audioConfig ?? DEFAULT_AUDIO_CONFIG} />
    ))
  ), [remoteParticipantIds, userTableProps.current]);

  const handleSeatClick = useCallback((tableIndex: number, seatIndex: number, userId: string) => {
    const tableRadius = tables[tableIndex].radius;
    const numSeats = tables[tableIndex].numSeats;
    const audioConfig = seatIndex === -1 ? DEFAULT_AUDIO_CONFIG : getAudioConfig(tableRadius, numSeats);
    userTableProps.current[userId] = { tableIndex, seatIndex, audioConfig };
    if (userId === localParticipantId) sendUserTableValueMessage('*');
  }, []);

  const sendUserTableValueMessage = useCallback((userId: string) => {
    if (userId !== '*') userTableProps.current[userId] = unseatedUserTableProps;
    if (!userTableProps.current[localParticipantId] && localParticipantId !== '') userTableProps.current[localParticipantId] = unseatedUserTableProps;
    const localUserTableProps = userTableProps.current[localParticipantId];
    if (!localUserTableProps) return console.error('no localUserId, not sending set-user-table-value');
    const userTableValueMessage: SetUserTableValueWorldMessage = {
      messageType: 'world',
      worldMessageType: 'set-user-table-value',
      seatedAt: new Date(),
      tablePosition: {
        position: {
          tableIndex: localUserTableProps.tableIndex,
          seatIndex: localUserTableProps.seatIndex,
        },
        userId: localParticipantId,
      },
    };

    if (callObject?.meetingState() === 'joined-meeting') {
      try {
        // console.log('sending table msg to ', userId.substr(0,5), userTableValueMessage.tablePosition.position, userTableValueMessage.tablePosition.userId.substr(0,5));
        sendAppMessage(userTableValueMessage, userId);
      } catch (e) {
        console.warn('could not broadcast message', e);
      }
    }
  }, [callObject, sendAppMessage, localParticipantId]);

  return (
    <>
      <div id='canvas-container' ref={containerRef}>
        <CameraControlsProvider>
          <Canvas shadows
                  frameloop='demand'
                  dpr={dpr}
                  camera={{ position: [0, 1.5, 5.5], rotation: [0, 0, 0], fov: 90, near: 0.1, far: 100 }}
                  gl={{ antialias, powerPreference }}
          >
            {/* <PerformanceMonitor
              ms={250}
              iterations={10}
              threshold={0.3}
              // bounds={(refreshrate: number) => [30, 60]}
              factor={0.5}
              step={0.1}
              // flipflops={12}
              onIncline={(api) => {
                console.log('performance incline', api);
                // setDpr(window.devicePixelRatio)
                // setAntialias(true)
                // setPowerPreference('high-performance')
              }}
              onDecline={(api) => {
                console.log('performance decline', api);
                if (api.factor < 0.1) {
                  setDpr(window.devicePixelRatio / 4)
                  setAntialias(false)
                  setPowerPreference('low-power')
                }
              }}
              onFallback={(api) => {
                console.log('performance decline fallback', api);
                // setDpr(window.devicePixelRatio / 4)
                // setAntialias(false)
                // setPowerPreference('low-power')
              }}
            />  */}
            {/* Can put components within PerformanceMonitor and throttle them down via onDecline/onFallback too */}
            {/* PerformanceMonitor is unrealiable with frameloop=demand, as it depends on frameloop=always to measure fps */}

            {/* Lights */}
            <color attach="background" args={[0, 0, 0]} />
            <ambientLight intensity={1.0} color="#ffffff"/>
            {/* { lightElements } */}

            {/* Background */}
            {/* { backgroundAndFloorElement } */}
            <Floor bounds={bounds} />
            <Background bounds={bounds} />

            {dailyUserAvatars}

            {/* { circularVirtualTables } */}

            <TableCameraProvider>
              {tables.map((table) => (
                <CircularVirtualTable
                  ref={table.tableIndex === 0 ? tableRef : null}
                  key={table.tableIndex}
                  tableIndex={table.tableIndex}
                  position={table.position}
                  radius={table.radius}
                  numSeats={table.numSeats}
                  onSeatUpdate={handleSeatClick}
                />
              ))}
            </TableCameraProvider>

            {/* <TestUserAvatar audioConfig={audioConfig} userName={'test1'} position={new Vector3(6,1,-5)} rotation={new Euler(0,Math.PI - 0.2,0)} audioUrlOrSrc={'voice-test.mp3'} videoUrlOrSrc={'this-does-not-work-need-to-import-file-instead'} />
            <TestUserAvatar audioConfig={audioConfig}  userName={'test2'} position={new Vector3(-18,1,-3)} rotation={new Euler(0,Math.PI + 1.2,0)} audioUrlOrSrc={'voice-test2.mp3'} videoUrlOrSrc={'this-does-not-work-need-to-import-file-instead'} />
            <TestUserAvatar audioConfig={audioConfig}  userName={'test3'} position={new Vector3(1,1,8)} rotation={new Euler(0,0,0)} audioUrlOrSrc={'voice-test3.mp3'} videoUrlOrSrc={'this-does-not-work-need-to-import-file-instead'} /> */}

            {/* <CircularVirtualTable tableIndex={0} position={new Vector3(0,0.01,-15)} radius={6} numSeats={4} color={new Color('white')} onSeatUpdate={handleSeatClick} />
            <CircularVirtualTable tableIndex={1} position={new Vector3(-28,0.01,30)} radius={8} numSeats={9} color={new Color('white')} onSeatUpdate={handleSeatClick} />
            <CircularVirtualTable tableIndex={2} position={new Vector3(28,0.01,28)} radius={10} numSeats={12} color={new Color('white')} onSeatUpdate={handleSeatClick} /> */}

            {/* Postprocessing */}
            <EffectComposer enableNormalPass={false}>
              <Bloom luminanceThreshold={0} mipmapBlur luminanceSmoothing={0.0} intensity={1} />
              {/* <DepthOfField target={[0, 0, 13]} focalLength={0.3} bokehScale={15} height={700} /> */}
            </EffectComposer>

            {/* Small helper that freezes the shadows for better performance */}
            <BakeShadows />

            {/* Camera movements */}
            <CameraControls bounds={bounds} containerRef={containerRef} />
          </Canvas>
          <HUDMenu />
        </CameraControlsProvider>
      </div>
    </>
  )
}

export default React.memo(Scene);
